
import { useState, useCallback } from "react";
import { ArticleAuthor } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useAuthorOperations = () => {
  const [authors, setAuthors] = useState<ArticleAuthor[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadAuthors = useCallback(async (retryCount = 0, maxRetries = 3) => {
    if (loading) {
      console.log("Already loading authors, skipping request");
      return; // Prevent concurrent requests
    }
    
    try {
      setLoading(true);
      console.log("Loading authors...");
      
      const { data, error } = await supabase
        .from('article_authors')
        .select('*')
        .order('first_name');

      if (error) throw error;
      
      console.log("Authors loaded:", data?.length || 0);
      if (data && Array.isArray(data)) {
        setAuthors(data);
      } else {
        console.warn("No author data received or invalid format:", data);
        setAuthors([]);
      }
    } catch (error: any) {
      console.error('Error loading authors:', error);
      
      // Retry logic for network errors
      if (retryCount < maxRetries && 
          (error.message.includes('Failed to fetch') || 
           error.code === 'ERR_INSUFFICIENT_RESOURCES')) {
        console.log(`Retrying loadAuthors (${retryCount + 1}/${maxRetries})...`);
        setTimeout(() => {
          loadAuthors(retryCount + 1, maxRetries);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
        return;
      }
      
      toast({
        variant: "destructive",
        title: "Error loading authors",
        description: error.message || "Failed to load authors"
      });
    } finally {
      setLoading(false);
    }
  }, [loading, toast]);

  const createAuthor = async (authorData: Partial<ArticleAuthor>): Promise<ArticleAuthor | null> => {
    try {
      // Create the author entry
      const { data, error } = await supabase
        .from('article_authors')
        .insert({
          first_name: authorData.first_name,
          last_name: authorData.last_name || null,
          bio: authorData.bio || null,
          profile_image_url: authorData.profile_image_url || null,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Refresh authors list
      setAuthors(prevAuthors => [...prevAuthors, data]);
      
      toast({
        title: "Author created",
        description: "The author has been successfully created."
      });
      
      return data;
    } catch (error: any) {
      console.error('Error creating author:', error);
      toast({
        variant: "destructive",
        title: "Error creating author",
        description: error.message || "Failed to create author"
      });
      return null;
    }
  };

  const updateAuthor = async (id: string, authorData: Partial<ArticleAuthor>): Promise<boolean> => {
    try {
      // Update the author record
      const { error } = await supabase
        .from('article_authors')
        .update({
          first_name: authorData.first_name,
          last_name: authorData.last_name,
          bio: authorData.bio,
          profile_image_url: authorData.profile_image_url
        })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state to reflect changes
      setAuthors(prevAuthors => 
        prevAuthors.map(author => 
          author.id === id ? { ...author, ...authorData } : author
        )
      );
      
      toast({
        title: "Author updated",
        description: "The author has been successfully updated."
      });
      
      return true;
    } catch (error: any) {
      console.error('Error updating author:', error);
      toast({
        variant: "destructive",
        title: "Error updating author",
        description: error.message || "Failed to update author"
      });
      return false;
    }
  };

  const deleteAuthor = async (id: string): Promise<boolean> => {
    try {
      // First check if any articles are using this author
      const { data: articlesUsingAuthor, error: checkError } = await supabase
        .from('articles')
        .select('id')
        .eq('author_id', id);
        
      if (checkError) throw checkError;
      
      // If author is in use, don't allow deletion
      if (articlesUsingAuthor && articlesUsingAuthor.length > 0) {
        throw new Error(`Cannot delete author because they are used by ${articlesUsingAuthor.length} article(s).`);
      }
      
      const { error } = await supabase
        .from('article_authors')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setAuthors(authors.filter(author => author.id !== id));
      
      toast({
        title: "Author deleted",
        description: "The author has been successfully deleted."
      });
      
      return true;
    } catch (error: any) {
      console.error('Error deleting author:', error);
      toast({
        variant: "destructive",
        title: "Error deleting author",
        description: error.message || "Failed to delete author"
      });
      return false;
    }
  };

  return {
    authors,
    loading,
    loadAuthors,
    createAuthor,
    updateAuthor,
    deleteAuthor
  };
};
